import React from 'react';
import { ArrowUpDown, TrendingUp, TrendingDown } from 'lucide-react';

/**
 * Компонент для учета перепада высот в трубопроводе
 */
const ElevationSection = ({ formData, handleInputChange, errors }) => {
  if (!formData.includeElevationChange) {
    return (
      <div className="mb-6">
        <div className="flex items-center">
          <label className="inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              name="includeElevationChange"
              checked={formData.includeElevationChange}
              onChange={handleInputChange}
              className="form-checkbox"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Учитывать перепад высот (изменение высоты между началом и концом трубы)
            </span>
          </label>
        </div>
      </div>
    );
  }

  // Определение иконки и стиля на основе значения перепада высот
  const elevationChange = parseFloat(formData.elevationChange) || 0;
  const ElevationIcon = elevationChange > 0 ? TrendingUp : (elevationChange < 0 ? TrendingDown : ArrowUpDown);
  const elevationClass = elevationChange > 0 ? 'text-orange-500' : (elevationChange < 0 ? 'text-green-500' : 'text-blue-500');

  return (
    <div className="mb-6">
      <div className="flex items-center mb-4">
        <label className="inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            name="includeElevationChange"
            checked={formData.includeElevationChange}
            onChange={handleInputChange}
            className="form-checkbox"
          />
          <span className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
            Учитывать перепад высот
          </span>
        </label>
      </div>

      <div className="bg-teal-50 dark:bg-teal-900/20 p-4 rounded-lg border border-teal-100 dark:border-teal-800">
        <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-3 flex items-center">
          <ArrowUpDown className="w-4 h-4 mr-2 text-teal-600 dark:text-teal-400" />
          Изменение высоты трубопровода
        </h4>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Изменение высоты */}
          <div>
            <label htmlFor="elevationChange" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <ElevationIcon className={`w-4 h-4 mr-2 ${elevationClass}`} />
              Перепад высот (м)
            </label>
            <div className="relative">
              <input
                type="text"
                id="elevationChange"
                name="elevationChange"
                value={formData.elevationChange}
                onChange={handleInputChange}
                className={`form-input w-full ${errors.elevationChange ? 'input-error' : ''}`}
                placeholder="Введите перепад (+ вверх, - вниз)"
              />
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <span className="text-sm text-gray-400">м</span>
              </div>
            </div>
            {errors.elevationChange && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.elevationChange}
              </p>
            )}
            <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
              Положительное значение означает подъем (конец трубы выше начала).
              Отрицательное значение - спуск (конец трубы ниже начала).
            </p>
          </div>

          {/* Гидростатическое давление */}
          <div className="flex flex-col justify-center">
            <div className="p-3 bg-white dark:bg-gray-800 rounded-lg border border-teal-100 dark:border-teal-800">
              <div className="text-sm font-medium mb-1">Гидростатическое давление:</div>
              <div className="text-xl font-bold text-teal-700 dark:text-teal-300">
                {Math.abs(elevationChange * 0.0981).toFixed(3)} бар
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">
                {elevationChange > 0 ? (
                  "Подъем требует дополнительной энергии и увеличивает потребное давление"
                ) : elevationChange < 0 ? (
                  "Спуск снижает требуемую энергию и увеличивает расход"
                ) : (
                  "Нет перепада высот"
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Информация о влиянии перепада высот */}
        <div className="mt-4 p-3 bg-white dark:bg-gray-800 rounded-lg border border-teal-100 dark:border-teal-800 text-xs text-gray-600 dark:text-gray-400">
          <h5 className="font-medium text-gray-900 dark:text-white mb-1">Справочная информация:</h5>
          <ul className="list-disc pl-4 space-y-1">
            <li>Каждые 10 метров подъема создают противодавление около 1 бар</li>
            <li>Точное значение: 1 метр водяного столба = 0.0981 бар</li>
            <li>При подъеме воды требуется дополнительная энергия (давление)</li>
            <li>При спуске воды гравитация помогает движению и снижает требуемое давление</li>
            <li>В замкнутых системах (например, отопление) общий перепад высот равен нулю</li>
          </ul>
          <p className="mt-2">
            Учет перепада высот особенно важен для насосных систем и систем водоснабжения 
            многоэтажных зданий.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ElevationSection;